import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputBase from '@mui/material/InputBase';
import AppButton from '../../../components/common/AppButton';
import { ConditionChip, TagChip, OwnerAvatar } from '../../../components/common/Bits';
import { inr, personSub } from '../../../utils/format';

export function StageSelect({ value, stages, onChange, showBorder = false }) {
  return (
    <Select
      variant="standard"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      input={
        <InputBase
          sx={{
            fontSize: 11,
            border: '1px solid var(--border)',
            borderLeft: showBorder
              ? `3px solid ${(stages.find((s) => s.k === value) || {}).c || 'var(--muted)'}`
              : '1px solid var(--border)',
            borderRadius: '7px',
            background: 'var(--card-2)',
            color: 'var(--ink)',
            p: '4px 6px',
            '& .MuiInputBase-input': { p: 0 },
          }}
        />
      }
    >
      {stages.map((s) => (
        <MenuItem key={s.k} value={s.k} sx={{ fontSize: 12 }}>
          {s.k}
        </MenuItem>
      ))}
    </Select>
  );
}

/** Kanban card for one lead. */
export default function LeadCard({ channelId, lead, stages, onStage, onEdit, onDelete, onConvert, canEdit = true }) {
  const loc = [lead.city, lead.mandal, lead.district, lead.state].filter(Boolean).join(', ');
  const sub = channelId === 'd2c' ? personSub(lead) || '—' : lead.contact || '—';

  return (
    <Box
      sx={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
        p: '11px',
        boxShadow: 'var(--shadow)',
      }}
    >
      <Box sx={{ fontFamily: 'var(--disp)', fontWeight: 600, fontSize: 13.5, lineHeight: 1.25 }}>{lead.name}</Box>
      <Box sx={{ fontSize: 11.5, color: 'var(--ink-2)', mt: '3px' }}>
        {sub}{lead.phone ? ` · ${lead.phone}` : ''}
      </Box>
      {(loc || lead.pincode) && (
        <Box sx={{ fontSize: 11.5, color: 'var(--ink-2)', mt: '3px' }}>
          📍 {loc}{lead.pincode ? ` - ${lead.pincode}` : ''}
        </Box>
      )}
      <Box sx={{ fontFamily: 'var(--disp)', fontWeight: 700, color: 'var(--side)', mt: '7px', fontSize: 15 }}>
        {inr(lead.value || 0)}
        <Box component="span" sx={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}> /mo est.</Box>
      </Box>
      <Box sx={{ fontSize: 11.5, color: 'var(--ink-2)', mt: 0.75 }}>
        <TagChip>{lead.source || '—'}</TagChip>
        <OwnerAvatar name={lead.owner} />
      </Box>
      {channelId === 'd2c' && lead.conditions?.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap' }}>
          {lead.conditions.map((c) => (
            <ConditionChip key={c}>{c}</ConditionChip>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '9px', gap: 0.75 }}>
        {canEdit ? (
          <StageSelect value={lead.stage} stages={stages} onChange={onStage} />
        ) : (
          <TagChip>{lead.stage}</TagChip>
        )}
        {canEdit && (
          <Box sx={{ display: 'flex', gap: '4px' }}>
            {lead.stage === 'Won' && (
              <AppButton mini tone="gold" onClick={onConvert} title="Convert to account">→ Acct</AppButton>
            )}
            <AppButton mini onClick={onEdit}>Edit</AppButton>
            <AppButton mini tone="danger" onClick={onDelete}>✕</AppButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}
